/**
 * Policy Engine — Load, validate, and expose the governance policy.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.3
 *
 * The Policy Engine is the single access point for all governance rules.
 * All validators query the policy engine instead of importing scattered constants.
 *
 * Design principle N7: Per-profile capable (future-proofs for audience profiles).
 */

import type {
  GovernancePolicy,
  VisualFamilyRule,
  SupportFamilyRule,
  QuestionTypeRule,
} from './policyTypes'
import type { GateThresholds } from './issueContract'
import type { ModuleId, QuestionType } from '../../content'
import { getTierConfig, type TierConfig } from '../../content'
import { DEFAULT_POLICY } from './defaultPolicy'
import { buildVisualPolicyFromMap } from './visualPolicyBuilder'
import { getModuleRulebook } from '../rulebooks'
import type { CognitiveLevel } from '../cognitiveLevel'
// C1 (hardening-audit §1): profile-aware cognitive floor SSOT. NOTE: tierBands
// already imports getTierBand from this module; getCognitiveFloorForBand is
// only invoked inside getPlusRequirements (runtime), never at module-eval time,
// so this import cycle is lazy-only and safe (same pattern as the existing edge).
import { getCognitiveFloorForBand, type TierBand } from '../tierBands'
import {
  SEVERITY,
  severityValue,
  type SeverityLevelName,
  type ScoringWeights,
} from './severityScale'
import {
  loadGovernanceConfig,
  getDisabledStages,
  type GovernanceConfigOverrides,
} from './governanceConfigStore'
import { error as logError } from '../../utils/logger'

// ─── Singleton Policy Instance ────────────────────────────────────────────────

let _activePolicy: GovernancePolicy = DEFAULT_POLICY
let _initialized = false
let _visualPolicyResolved = false
let _visualPolicyFailures = 0
let _visualPolicyPermanentlyFailed = false
let _storedConfig: GovernanceConfigOverrides | null = null

/**
 * Monotonically-increasing counter bumped every time the active policy is
 * replaced (either via initializePolicy or ensureVisualPolicy). Consumers that
 * want batch-consistency can capture this at the start of a pipeline run and
 * compare at the end — a mismatch means the policy was swapped mid-batch and
 * the batch's verdict may have been computed against two different versions.
 *
 * P1-5 / C14 (mei 2026): introduced as part of the singleton-lock pattern.
 */
let _policyVersion = 0

/**
 * Internal helper — the ONLY place where _activePolicy gets reassigned.
 *
 * Performs an atomic swap:
 *   1. Object.freeze(next) at the top level so consumers cannot accidentally
 *      mutate fields on the live policy. Nested objects are intentionally not
 *      deep-frozen to keep the cost bounded; mutation of nested objects has
 *      never been a real risk because all stage helpers treat the policy as
 *      read-only.
 *   2. Single assignment to _activePolicy — atomic at the JS-engine level.
 *      Any consumer that called getPolicy() before this point holds a stable
 *      reference to the previous policy and is unaffected.
 *   3. Bump _policyVersion so external observers can detect the swap.
 *
 * Keeping all swaps behind this helper means future fields (e.g. tier-band
 * recalibration, per-profile overrides) inherit the same guarantees without
 * adding new mutation paths to audit.
 */
function replacePolicy(next: GovernancePolicy): void {
  _activePolicy = Object.freeze(next)
  _policyVersion++
}

/**
 * Maximum number of times ensureVisualPolicy() will retry buildVisualPolicyFromMap
 * before giving up and entering permanently-degraded mode.
 *
 * Without this cap, every getPolicy() call after a persistent failure would
 * (a) re-invoke the expensive seed scan and (b) emit another logError line —
 * resulting in log spam and per-call CPU tax during dashboard validation runs.
 */
const MAX_VISUAL_POLICY_RETRIES = 3

/**
 * Ensure the visual policy is populated from seed data.
 * Called lazily to avoid circular dependency at module init time.
 *
 * C15 (Sprint 1): catch errors are logged instead of silently swallowed.
 * V5 (Sprint 4): retries are capped at MAX_VISUAL_POLICY_RETRIES; after that
 * the engine enters permanently-degraded mode (no more retries, no more log
 * spam) and continues with the default empty visual policy. Admin diagnostics
 * surface this via isVisualPolicyDegraded().
 */
function ensureVisualPolicy(): void {
  if (_visualPolicyResolved) return
  if (_visualPolicyPermanentlyFailed) return

  try {
    const visual = buildVisualPolicyFromMap()
    replacePolicy({ ..._activePolicy, visual })
    _visualPolicyResolved = true
  } catch (e) {
    _visualPolicyFailures++
    logError(
      `[policyEngine] Visual policy build failed (attempt #${_visualPolicyFailures}/${MAX_VISUAL_POLICY_RETRIES}):`,
      e,
    )
    if (_visualPolicyFailures >= MAX_VISUAL_POLICY_RETRIES) {
      _visualPolicyPermanentlyFailed = true
      logError(
        `[policyEngine] Visual policy build PERMANENTLY DEGRADED after ` +
          `${MAX_VISUAL_POLICY_RETRIES} attempts — falling back to empty visual ` +
          `policy. Visual-stage answer-leakage checks will run against the ` +
          `default policy until app restart.`,
      )
    }
    // Below the cap: silent skip — will retry on next getPolicy() access
  }
}

/**
 * Telemetry: how many times has ensureVisualPolicy() failed since init?
 * Admin diagnostics can surface this; >0 = visual policy is in degraded state
 * and visual-stage answer-leakage checks may be running on the default
 * (empty) visual policy instead of the seed-derived policy.
 */
export function getVisualPolicyBuildFailures(): number {
  return _visualPolicyFailures
}

/**
 * Returns true if the visual policy has been successfully resolved from seeds.
 * If false AND getVisualPolicyBuildFailures() > 0, the system is degraded.
 */
export function isVisualPolicyResolved(): boolean {
  return _visualPolicyResolved
}

/**
 * Returns true when the visual policy has been permanently abandoned after
 * MAX_VISUAL_POLICY_RETRIES failed builds. Admin UI can surface this as a
 * prominent "Visual policy degraded — visual answer-leakage checks disabled"
 * banner. Resolution requires an app restart (or a code-level reset).
 */
export function isVisualPolicyDegraded(): boolean {
  return _visualPolicyPermanentlyFailed
}

/**
 * Initialize the policy engine with a policy object.
 * Call once at startup. Defaults to DEFAULT_POLICY if not called.
 *
 * ENHANCED: Now also loads stored governance config overrides (CONFLICT-03 resolution).
 * ENHANCED: Lazily resolves visual policy from seeds to avoid circular init crash.
 */
export async function initializePolicy(
  policy?: GovernancePolicy,
): Promise<void> {
  replacePolicy(policy ?? DEFAULT_POLICY)
  _initialized = true

  // Resolve visual policy from seeds (safe now — all modules loaded)
  ensureVisualPolicy()

  // Load stored config overrides
  _storedConfig = await loadGovernanceConfig()

  // Validate policy integrity
  const errors = validatePolicyIntegrity(_activePolicy)
  if (errors.length > 0) {
    logError('🚨 POLICY ENGINE: Integrity errors found:')
    errors.forEach((e) => logError(`  ❌ ${e}`))
    if (import.meta.env?.DEV) {
      throw new Error(`Policy has ${errors.length} integrity error(s)`)
    }
  }
}

/**
 * Get the active governance policy.
 *
 * The returned object is shallow-frozen — direct field mutation throws in
 * strict mode and is silently dropped otherwise. Consumers that need
 * batch-consistency should bind the reference locally (e.g.
 * `const policy = getPolicy()`) instead of calling `getPolicy()` repeatedly,
 * so a mid-batch `initializePolicy()` cannot cause a split read.
 */
export function getPolicy(): GovernancePolicy {
  // Lazy-resolve visual policy on first access if not yet done
  if (!_visualPolicyResolved) {
    ensureVisualPolicy()
  }
  return _activePolicy
}

/**
 * Check if the policy engine has been explicitly initialized.
 */
export function isPolicyInitialized(): boolean {
  return _initialized
}

/**
 * Get the stored governance config overrides.
 * Returns null if no overrides are stored.
 */
export async function getStoredConfig(): Promise<GovernanceConfigOverrides | null> {
  if (!_storedConfig) {
    _storedConfig = await loadGovernanceConfig()
  }
  return _storedConfig
}

/**
 * Refresh stored config from dataLayer.
 * Call after GovernanceSettingsTab saves new settings.
 */
export async function refreshStoredConfig(): Promise<void> {
  _storedConfig = await loadGovernanceConfig()
}

/**
 * Get pipeline stages that should be skipped based on stored config.
 */
export async function getSkipStages(): Promise<number[]> {
  const config = await getStoredConfig()
  if (!config) return []
  return getDisabledStages(config)
}

// ─── Plus-Level Queries ───────────────────────────────────────────────────────

/**
 * Check if a tier is a plus-level tier.
 * Plus tiers demand higher cognitive level, more complex problems,
 * and transfer-oriented questions compared to their base counterpart.
 */
export function isPlusTier(tier: number): boolean {
  const config = getTierConfig(tier)
  return config?.isPlus ?? false
}

/**
 * Get the base (non-plus) tier for a given plus tier.
 * E.g., M7+ (tier 10) → M7 (tier 9), B6+ (tier 2) → B6 (tier 1)
 * Returns the same tier if it's already a base tier.
 */
export function getBaseTierForPlus(tier: number): number {
  const config = getTierConfig(tier)
  if (!config || !config.isPlus) return tier
  return tier - 1 // Plus tiers are always base + 1
}

/**
 * Get plus-level content requirements for a tier.
 * Returns null for non-plus tiers.
 *
 * Plus-level requirements (§3.3 Master Rulebook):
 * - minCognitiveLevel: Minimum cognitive level (application or analysis)
 * - minDidacticDepth: Minimum didactic depth (2=begrip or 3=transfer)
 * - minCitoDifficulty: Higher minimum citoDifficulty
 * - requireSolutionSteps: Whether solutionSteps are mandatory
 * - minSolutionSteps: Minimum number of solution steps
 * - requireMultiStep: Whether multi-step reasoning is expected
 */
export function getPlusRequirements(
  tier: number,
  module?: string,
): {
  minCognitiveLevel: CognitiveLevel
  minDidacticDepth: 2 | 3
  minCitoDifficulty: number
  requireSolutionSteps: boolean
  minSolutionSteps: number
  requireMultiStep: boolean
} | null {
  if (!isPlusTier(tier)) return null

  const band = getTierBand(tier)
  // C1 (hardening-audit §1): minCognitiveLevel is now PROFILE-AWARE via the
  // COGNITIVE_FLOOR_BY_PROFILE SSOT (tierBands.ts), replacing the formerly
  // hardcoded application/analysis values. procedural-math is preserved
  // verbatim; language/comprehension cap at comprehension on expert; recall
  // -knowledge demands no escalation. The other plus requirements
  // (didacticDepth/cito/steps) are unchanged.
  const minCognitiveLevel = getCognitiveFloorForBand(band as TierBand, module)

  switch (band) {
    case 'basic': // B6+, M6+ (tiers 2, 4)
      return {
        minCognitiveLevel,
        minDidacticDepth: 2,
        minCitoDifficulty: 0.25,
        requireSolutionSteps: false,
        minSolutionSteps: 2,
        requireMultiStep: false,
      }
    case 'intermediate': // E6+, B7+ (tiers 6, 8)
      return {
        minCognitiveLevel,
        minDidacticDepth: 2,
        minCitoDifficulty: 0.3,
        requireSolutionSteps: true,
        minSolutionSteps: 3,
        requireMultiStep: true,
      }
    case 'advanced': // M7+, E7+ (tiers 10, 12)
      return {
        minCognitiveLevel,
        minDidacticDepth: 3,
        minCitoDifficulty: 0.35,
        requireSolutionSteps: true,
        minSolutionSteps: 3,
        requireMultiStep: true,
      }
    case 'expert': // E8+ (tier 16)
      return {
        minCognitiveLevel,
        minDidacticDepth: 3,
        minCitoDifficulty: 0.4,
        requireSolutionSteps: true,
        minSolutionSteps: 4,
        requireMultiStep: true,
      }
    default:
      return null
  }
}

// ─── Policy Queries ───────────────────────────────────────────────────────────

/**
 * Get the tier band key for a tier number.
 * basic (1-4), intermediate (5-8), advanced (9-12), expert (13-16)
 */
export function getTierBand(tier: number): string {
  if (tier <= 4) return 'basic'
  if (tier <= 8) return 'intermediate'
  if (tier <= 12) return 'advanced'
  return 'expert'
}

/**
 * Get forbidden vocabulary for a tier.
 */
export function getForbiddenVocab(tier: number): string[] {
  const band = getTierBand(tier)
  return _activePolicy.vocab.forbiddenByTierBand[band] ?? []
}

/**
 * Get maximum question text length for a tier.
 */
export function getMaxQuestionLength(tier: number, module?: ModuleId): number {
  const band = getTierBand(tier)

  // Check module override first
  if (module) {
    const moduleOverride =
      _activePolicy.contentBudgets.moduleOverrides?.[module]
    if (moduleOverride?.maxQuestionLengthByTierBand?.[band]) {
      return moduleOverride.maxQuestionLengthByTierBand[band]
    }
  }

  return _activePolicy.contentBudgets.maxQuestionLengthByTierBand[band] ?? 800
}

/**
 * Get minimum explanation length for a tier.
 */
export function getMinExplanationLength(tier: number): number {
  const band = getTierBand(tier)
  return _activePolicy.contentBudgets.minExplanationLengthByTierBand[band] ?? 30
}

/**
 * Get minimum distractor explanation length for a tier.
 */
export function getMinDistractorLength(tier: number): number {
  const band = getTierBand(tier)
  return _activePolicy.contentBudgets.minDistractorLengthByTierBand[band] ?? 15
}

/**
 * Get maximum context field length for a tier (TAA/STU reading passages).
 */
export function getMaxContextLength(tier: number): number {
  const band = getTierBand(tier)
  return _activePolicy.contentBudgets.maxContextLengthByTierBand?.[band] ?? 2000
}

/**
 * Get the maximum sentence length (in words) for a tier before a
 * "long sentence" coach issue should fire. Phase 4 centralisation —
 * previously a 25-word literal inline in textQualityChecks.
 */
export function getMaxSentenceLength(tier: number): number {
  const band = getTierBand(tier)
  return (
    _activePolicy.textQuality?.maxSentenceLengthByTierBand[band] ??
    DEFAULT_POLICY.textQuality.maxSentenceLengthByTierBand[band] ??
    25
  )
}

/**
 * Get the active scoring weights used by computeIssuePenalty.
 * Pipeline call sites (gate/evaluate.ts, orchestrator.ts) pass this to
 * `computeIssuePenalty(issues, getScoringWeights())` so admin-tuned
 * overrides take effect without restarting the engine.
 */
export function getScoringWeights(): ScoringWeights {
  return _activePolicy.scoring ?? DEFAULT_POLICY.scoring
}

// NOTE: stab-ronde 3 (mei 2026) — `getSeverityLevel` verwijderd: nul callers.
// Was een 3-regel wrapper rond severityValue(). Validators die een semantische
// severity-lookup nodig hebben gebruiken nu direct `SEVERITY.<NAAM>` (constant)
// of importeren `severityValue` uit `./severityScale`.

/** Re-export the semantic severity taxonomy for direct named-constant use. */
export { SEVERITY }
export type { SeverityLevelName, ScoringWeights }

/**
 * Get visual policy for a specific family.
 */
export function getVisualPolicy(familyId: string): VisualFamilyRule | null {
  return _activePolicy.visual.familyRules[familyId] ?? null
}

/**
 * Check if a family requires a visual aid.
 */
export function familyRequiresVisual(familyId: string): boolean {
  return _activePolicy.visual.requiredFamilies.includes(familyId)
}

/**
 * Get support policy for a specific family.
 */
export function getSupportPolicy(familyId: string): SupportFamilyRule | null {
  return _activePolicy.support.familyRules[familyId] ?? null
}

/**
 * Get question type validation rules.
 */
export function getQuestionTypeRule(
  type: QuestionType | string,
): QuestionTypeRule | null {
  return _activePolicy.questionType.typeRules[type] ?? null
}

/**
 * Check if a question type is assessment-eligible.
 */
export function isAssessmentEligibleType(type: QuestionType | string): boolean {
  const rule = getQuestionTypeRule(type)
  return rule?.assessmentEligible ?? false
}

/**
 * Get notation requirements for a module.
 *
 * Phase 3: Consults the module rulebook first, then falls back to
 * DEFAULT_POLICY. This lets per-module didactic rules override the
 * global policy without touching DEFAULT_POLICY.
 */
export function getNotationRequirements(module: ModuleId): {
  requireLatexFractions: boolean
  requireCommaDecimals: boolean
  requireSuperscriptUnits: boolean
  requireEuroComma: boolean
} {
  const rb = getModuleRulebook(module)
  return {
    requireLatexFractions:
      rb?.notation.requireLatexFractions ??
      _activePolicy.notation.latexFractionModules.includes(module),
    requireCommaDecimals:
      rb?.notation.requireCommaDecimals ??
      _activePolicy.notation.commaDecimalModules.includes(module),
    requireSuperscriptUnits:
      rb?.notation.requireSuperscriptUnits ??
      _activePolicy.notation.superscriptUnitModules.includes(module),
    requireEuroComma:
      rb?.notation.requireEuroComma ??
      _activePolicy.notation.euroCommaModules.includes(module),
  }
}

/**
 * Get distractor misconception pattern for a module.
 *
 * Phase 3: Rulebook entry wins; falls back to DEFAULT_POLICY.
 */
export function getDistractorPattern(module: ModuleId): RegExp | null {
  // O1-A (hardening-audit §4): pure rulebook read. The registry is exhaustive
  // over ModuleId (rulebookExhaustiveness.test.ts), so a valid module always
  // resolves a rulebook; the former `?? defaultPolicy.distractor.modulePatterns`
  // fallback was structurally dead and has been removed.
  const rb = getModuleRulebook(module)
  const pattern = rb?.content.distractorMisconceptionPattern
  if (!pattern) return null
  return new RegExp(pattern, 'i')
}

/**
 * Check if a module requires rule references in explanations.
 *
 * Phase 3: Rulebook entry wins; falls back to DEFAULT_POLICY.
 */
export function requiresRuleReference(module: ModuleId): boolean {
  // O1-A (hardening-audit §4): pure rulebook read; the defaultPolicy fallback
  // was structurally dead (registry exhaustive over ModuleId). `false` only
  // for an unknown/undefined module.
  const rb = getModuleRulebook(module)
  return rb?.explanation.requireRuleReference ?? false
}

/**
 * Get the minimum tier for check strategy requirement in a module.
 *
 * Phase 3: Rulebook entry wins; falls back to DEFAULT_POLICY.
 */
export function getCheckStrategyMinTier(module: ModuleId): number | null {
  // O1-A (hardening-audit §4): pure rulebook read; the defaultPolicy fallback
  // was structurally dead (registry exhaustive over ModuleId). `null` only for
  // an unknown/undefined module.
  const rb = getModuleRulebook(module)
  return rb?.explanation.checkStrategyMinTier ?? null
}

/**
 * Get the preferred strategy keywords for a module (Phase 3).
 * Used by the pedagogy stage as a soft hint at higher tiers.
 */
export function getPreferredStrategyKeywords(module: ModuleId): string[] {
  const rb = getModuleRulebook(module)
  return rb?.explanation.preferredStrategyKeywords ?? []
}

/**
 * Get the list of preferred question types for a module (Phase 3).
 * Empty array when no rulebook exists.
 */
export function getPreferredQuestionTypes(
  module: ModuleId,
): (QuestionType | string)[] {
  const rb = getModuleRulebook(module)
  return rb?.questionTypes.preferredTypes ?? []
}

/**
 * Get gate thresholds.
 */
export function getGateThresholds(): GateThresholds {
  return _activePolicy.gates
}

/**
 * Check if a governance policy feature flag is enabled.
 * NOTE: Renamed from isFeatureEnabled to avoid collision with config/featureFlags.ts (FF-3).
 */
export function isPolicyFeatureEnabled(
  flag: keyof GovernancePolicy['featureFlags'],
): boolean {
  return _activePolicy.featureFlags[flag] ?? false
}

// ─── Policy Validation ────────────────────────────────────────────────────────

/**
 * Validate policy integrity.
 * Returns array of error strings (empty = valid).
 */
export function validatePolicyIntegrity(policy: GovernancePolicy): string[] {
  const errors: string[] = []

  // Version check
  if (!policy.version || !/^\d+\.\d+\.\d+$/.test(policy.version)) {
    errors.push(`Invalid policy version: "${policy.version}" (expected semver)`)
  }

  // Gate thresholds sanity
  if (policy.gates.productionMinScore > policy.gates.assessmentMinScore) {
    errors.push('Production min score should not exceed assessment min score')
  }
  if (
    policy.gates.productionMinScore < 0 ||
    policy.gates.productionMinScore > 100
  ) {
    errors.push('Production min score must be 0-100')
  }
  if (
    policy.gates.assessmentMinScore < 0 ||
    policy.gates.assessmentMinScore > 100
  ) {
    errors.push('Assessment min score must be 0-100')
  }

  // Content budgets: min explanation should be less than max question length
  const tierBands = ['basic', 'intermediate', 'advanced', 'expert']
  for (const band of tierBands) {
    const maxQ = policy.contentBudgets.maxQuestionLengthByTierBand[band]
    const minE = policy.contentBudgets.minExplanationLengthByTierBand[band]
    if (maxQ === undefined) {
      errors.push(`Missing maxQuestionLength for tier band "${band}"`)
    }
    if (minE === undefined) {
      errors.push(`Missing minExplanationLength for tier band "${band}"`)
    }
  }

  // Visual policy: required families should have rules
  for (const familyId of policy.visual.requiredFamilies) {
    if (!policy.visual.familyRules[familyId]) {
      errors.push(
        `Visual-required family "${familyId}" has no visual rules defined`,
      )
    }
  }

  // At least one audience profile
  if (policy.audienceProfiles.length === 0) {
    errors.push('At least one audience profile is required')
  }

  return errors
}

/**
 * Returns the current policy version (monotonic counter, starts at 0,
 * bumped on every replacement). Useful for diagnostics and for batch
 * orchestrators that want to detect mid-batch policy swaps.
 *
 * P1-5 / C14 (mei 2026): added as part of the singleton-lock pattern.
 */
export function getPolicyVersion(): number {
  return _policyVersion
}
